import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef, HostBinding } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { ITabSelectItem } from '../../common/tab-select/tab-select.component';
import { MatchService } from '../../services/match.service';
import { ContextService } from '../../services/context.service';
import { IShotsSummaryItem } from '../shots-summary/shots-summary.component';

@Component({
  selector: 'app-stats',
  templateUrl: './stats.component.html',
  styleUrls: ['./stats.component.scss']
})
export class StatsComponent implements OnInit, OnDestroy {
  _match: any;
  _events: any[] = [];
  _stats: any = {};
  section: ITabSelectItem;
  @Input() get match() { return this._match; }
           set match(match: any) {
             this._match = match;
             this.playersHome = match.home.players;
             this.playersVisitor = match.visitor.players;
           }
  @Input()  get events() { return this._events; }
            set events(events: any[]) {
              this._events = events;
              this.calculateShotsStats();
            }
  @Input()  get stats(): any { return this._stats; }
            set stats(stats: any) {
              this._stats = this.parseStats(stats);
              this.parseTableStats();
            }

  tabOptions: ITabSelectItem[];

  rowsHome = [];
  rowsVisitor = [];

  shotsStatsHome: IShotsSummaryItem[] = [];
  shotsStatsVisitor: IShotsSummaryItem[] = [];
  scrolledTeam = 'home';
  scrollCallback: any;

  _playersHome: any[] = [];
  _playersVisitor: any[] = [];

  get playersHome() { return this._playersHome; }
  set playersHome(players) {
    this.mergePlayers(this._playersHome, players);
  }

  get playersVisitor() {
    return this._playersVisitor;
  }
  set playersVisitor(players) {
    this.mergePlayers(this._playersVisitor, players);
  }


  @HostBinding('class.fixedHeader') scrollEffectActive = false;

  @ViewChild('homeBadges') homeBadges: ElementRef;
  @ViewChild('visitorBadges') visitorBadges: ElementRef;
  @ViewChild('header') headerElement: ElementRef;

  constructor(
    private hostElement: ElementRef,
    private sanitizer: DomSanitizer,
    private matchService: MatchService,
    private context: ContextService,
    private translateService: TranslateService
  ) {
    this.tabOptions = [
      { value: 'points', label: this.translateService.instant('Points'), unit: this.translateService.instant('points') },
      { value: 'accuracy', label: this.translateService.instant('Accuracy'), unit: this.translateService.instant('% of success') },
      { value: 'rebounds', label: this.translateService.instant('Rebounds'), unit: this.translateService.instant('rebounds') },
      { value: 'assists', label: this.translateService.instant('Assists'), unit: this.translateService.instant('assists') },
      { value: 'rating', label: this.translateService.instant('Performance Index Rating'), unit: this.translateService.instant('rating') },
      { value: 'summary', label: this.translateService.instant('Summary') },
    ];
  }

  ngOnInit() {
    this.scrollCallback = this.onScroll.bind(this);
    this.section = this.tabOptions[0];
    window.addEventListener('scroll', this.scrollCallback);
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.scrollCallback);
  }

  scrollToHome() {
    const fixedHeader = document.querySelector('app-scoreboard header.small');
    const scrollMargin = fixedHeader ? fixedHeader.scrollHeight : 0;
    const homeTop = this.homeBadges ? this.homeBadges.nativeElement.offsetTop : 0;
    const headerHeight = this.headerElement.nativeElement.scrollHeight;
    const homeScrollPoint = homeTop - headerHeight - scrollMargin;
    window.scrollTo({
      top: homeScrollPoint,
      behavior: 'smooth'
    });
  }

  scrollToVisitor() {
    const fixedHeader = document.querySelector('app-scoreboard header.small');
    const scrollMargin = fixedHeader ? fixedHeader.scrollHeight : 0;
    const visitorTop = this.visitorBadges ? this.visitorBadges.nativeElement.offsetTop : 0;
    const headerHeight = this.headerElement.nativeElement.scrollHeight;
    const visitorScrollPoint = visitorTop - headerHeight - scrollMargin;
    window.scrollTo({
      top: visitorScrollPoint,
      behavior: 'smooth'
    });
  }

  onScroll() {
    const currentScroll = document.documentElement.scrollTop;
    const fixedHeader = document.querySelector('app-scoreboard header.small');
    const fixedHeaderMargin = fixedHeader ? fixedHeader.scrollHeight : 0;
    const visitorTop = this.visitorBadges ? this.visitorBadges.nativeElement.offsetTop : 0;
    const statsHeaderTop = this.hostElement.nativeElement.offsetTop;
    const headerHeight = this.headerElement.nativeElement.scrollHeight;
    const visitorScrollPoint = visitorTop - headerHeight - fixedHeaderMargin - 20;
    const statsScrollPoint = (statsHeaderTop - fixedHeaderMargin);

    this.scrollEffectActive = currentScroll > statsScrollPoint;
    if (currentScroll > visitorScrollPoint) {
      this.scrolledTeam = 'visitor';
    } else {
      this.scrolledTeam = 'home';
    }
  }

  onStatsSectionChange(event) {
    this.section = event;
    this.parseTableStats();
  }

  parseTableStats() {
    if (this.section && this.section.value === 'summary') {
      this.rowsHome = this.match.home.players.reduce((acum, player) => {
        const stats = this.stats.stats[player.id];
        if (stats) {
          stats.player = player;
          stats.team = this.match.home.code;
          stats.player.image = player.img;
          acum.push(stats);
        }
        return acum;
      }, []);
      this.rowsVisitor = this.match.visitor.players.reduce((acum, player) => {
        const stats = this.stats.stats[player.id];
        if (stats) {
          stats.player = player;
          stats.team = this.match.visitor.code;
          stats.player.image = player.img;
          acum.push(stats);
        }
        return acum;
      }, []);
      this.calculateShotsStats();
    }
  }

  parseStats (data: any) {
    const bests = {
      points: { id: null, value: 0 },
      accuracy: { id: null, value: 0 },
      rebounds: { id: null, value: 0 },
      assists: { id: null, value: 0 },
      rating: { id: null, value: 0 },
      fouls: { id: null, value: 0 }
    };
    const keys = Object.keys(bests);
    const stats = data.reduce((acum, elem) => {
      acum[elem.player_id] = elem;
      let bestRebounder = null;
      keys.forEach((key) => {
        if (key !== 'rebounds') {
          if (bests[key].value < elem[key]) { bests[key] = { id: elem.player_id, value: elem[key] }; }
        } else {
          const reboundsSum = elem.rebounds_attack + elem.rebounds_defense;
          if (bests[key].value < reboundsSum) {
            bests[key] = { id: elem.player_id, value: reboundsSum };
            bestRebounder = elem;
          } else if (bests[key].value === reboundsSum) {
            if (!bestRebounder || bestRebounder.rebounds_attack < elem.rebounds_attack) {
              bests[key] = { id: elem.player_id, value: reboundsSum };
              bestRebounder = elem;
            }
          }
          elem.rebounds = `${elem.rebounds_defense} + ${elem.rebounds_attack}`;
          elem.reboundsSum = reboundsSum;
        }
      });
      return acum;
    }, {});

    return { stats, bests };
  }

  private calculateShotsStats() {
    if (this._events.length && this._match && this._match.home) {
      const shotsStats = {
        home: {
          double: {
            success: 0,
            total: 0,
            accuracy: 0
          },
          triple: {
            success: 0,
            total: 0,
            accuracy: 0
          },
          freeThrow: {
            success: 0,
            total: 0,
            accuracy: 0
          }
        },
        visitor: {
          double: {
            success: 0,
            total: 0,
            accuracy: 0
          },
          triple: {
            success: 0,
            total: 0,
            accuracy: 0
          },
          freeThrow: {
            success: 0,
            total: 0,
            accuracy: 0
          }
        }
      };
      const shots = this._events.reduce((acum, elem) => {
        let teamObj;
        let pointObj;
        if (elem.team_0 === this._match.home.id) {
          teamObj = acum.home;
        } else if (elem.team_0 === this._match.visitor.id) {
          teamObj = acum.visitor;
        }
        if (elem.points === 1 || elem.failures === 1) {
          pointObj = teamObj.freeThrow;
        } else if (elem.points === 2 || elem.failures === 2) {
          pointObj = teamObj.double;
        } else if (elem.points === 3 || elem.failures === 3) {
          pointObj = teamObj.triple;
        }

        if (pointObj) {
          pointObj.total += 1;
          if (!elem.failures) {
            pointObj.success += 1;
          }
        }

        return acum;
      }, shotsStats);

      // Calculate accuracy
      // It's ugly, but it does not need for callbacks nor Object.keys calls
      shots.home.freeThrow.accuracy = shots.home.freeThrow.total > 0 ?
        Math.round(shots.home.freeThrow.success / shots.home.freeThrow.total * 100) : 0;
      shots.home.double.accuracy = shots.home.double.total > 0 ?
        Math.round(shots.home.double.success / shots.home.double.total * 100) : 0;
      shots.home.triple.accuracy = shots.home.triple.total > 0 ?
        Math.round(shots.home.triple.success / shots.home.triple.total * 100) : 0;
      shots.visitor.freeThrow.accuracy = shots.visitor.freeThrow.total > 0 ?
        Math.round(shots.visitor.freeThrow.success / shots.visitor.freeThrow.total * 100) : 0;
      shots.visitor.double.accuracy = shots.visitor.double.total > 0 ?
        Math.round(shots.visitor.double.success / shots.visitor.double.total * 100) : 0;
      shots.visitor.triple.accuracy = shots.visitor.triple.total > 0 ?
        Math.round(shots.visitor.triple.success / shots.visitor.triple.total * 100) : 0;

      this.shotsStatsHome = [
        {
          title: this.translateService.instant('2P shots'),
          value: shots.home.double.success * 2,
          unit: this.translateService.instant('points'),
          shots: {
            success: shots.home.double.success,
            total: shots.home.double.total,
            accuracy: shots.home.double.accuracy
          }
        },
        {
          title: this.translateService.instant('3P shots'),
          value: shots.home.triple.success * 3,
          unit: this.translateService.instant('points'),
          shots: {
            success: shots.home.triple.success,
            total: shots.home.triple.total,
            accuracy: shots.home.triple.accuracy
          }
        },
        {
          title: this.translateService.instant('Free throw'),
          value: shots.home.freeThrow.success,
          unit: this.translateService.instant('points'),
          shots: {
            success: shots.home.freeThrow.success,
            total: shots.home.freeThrow.total,
            accuracy: shots.home.freeThrow.accuracy
          }
        }
      ];
      this.shotsStatsVisitor = [
        {
          title: this.translateService.instant('2P shots'),
          value: shots.visitor.double.success * 2,
          unit: this.translateService.instant('points'),
          shots: {
            success: shots.visitor.double.success,
            total: shots.visitor.double.total,
            accuracy: shots.visitor.double.accuracy
          }
        },
        {
          title: this.translateService.instant('3P shots'),
          value: shots.visitor.triple.success * 3,
          unit: this.translateService.instant('points'),
          shots: {
            success: shots.visitor.triple.success,
            total: shots.visitor.triple.total,
            accuracy: shots.visitor.triple.accuracy
          }
        },
        {
          title: this.translateService.instant('Free throw'),
          value: shots.visitor.freeThrow.success,
          unit: this.translateService.instant('points'),
          shots: {
            success: shots.visitor.freeThrow.success,
            total: shots.visitor.freeThrow.total,
            accuracy: shots.visitor.freeThrow.accuracy
          }
        }
      ];
    }
  }

  private mergePlayers(originalPlayers, playersWithNewStats) {
    const isSameTeam = originalPlayers.length === playersWithNewStats.length && originalPlayers.every((currentPlayer) => {
      return playersWithNewStats.some(_currentPlayer => currentPlayer.id === _currentPlayer.id);
    });

    if (!isSameTeam) {
      originalPlayers.splice(0, originalPlayers.length);
      originalPlayers.push(...playersWithNewStats);
    } else {
      originalPlayers.forEach(player => {
        const playerWithNewStats = playersWithNewStats.find(pl => player.id === pl.id);
        Object.keys(playerWithNewStats).forEach(key => {
          player[key] = playerWithNewStats[key];
        });
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/stats/stats/stats.component.ts